import { getIsAnyValueFalseOrInfinity } from 'utils/utils';

const Slopes_Gs = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 24];

// number of fences needed (per panel)
export const calculateNumberOfFencesNeeded = (
  widthOfPanel,
  lengthOfPanel,
  pitch,
  grndSnowLoad,
  rainLoad,
  fenceStrength
) => {
  if (
    rainLoad === '' ||
    getIsAnyValueFalseOrInfinity([widthOfPanel, lengthOfPanel, pitch, grndSnowLoad, fenceStrength])
  ) {
    return 'error';
  } else {
    return (
      Math.ceil(
        (((parseFloat(grndSnowLoad) + parseFloat(rainLoad)) *
          Math.cos(Math.atan(parseFloat(pitch) / 12)) *
          (parseFloat(widthOfPanel) / 12) *
          parseFloat(lengthOfPanel) *
          Math.sin(Math.atan(parseFloat(pitch) / 12))) /
          parseFloat(fenceStrength)) *
          10
      ) / 10
    );
  }
};

// maximum length reuiring 1 fence (feet)
export const calculateMaxLengthOfOneFenceNeeded = (
  widthOfPanel,
  lengthOfPanel,
  pitch,
  grndSnowLoad,
  rainLoad,
  fenceStrength
) => {
  if (
    rainLoad === '' ||
    getIsAnyValueFalseOrInfinity([widthOfPanel, lengthOfPanel, pitch, grndSnowLoad, fenceStrength])
  ) {
    return 'error';
  } else {
    return (
      parseFloat(fenceStrength) /
      ((parseFloat(grndSnowLoad) + parseFloat(rainLoad)) *
        Math.cos(Math.atan(parseFloat(pitch) / 12)) *
        (parseFloat(widthOfPanel) / 12) *
        Math.sin(Math.atan(parseFloat(pitch) / 12)))
    ).toFixed(2);
  }
};

// maximum length reuiring 2 fence (feet)
export const calculateMaxLengthOfTwoFenceNeeded = (
  widthOfPanel,
  lengthOfPanel,
  pitch,
  grndSnowLoad,
  rainLoad,
  fenceStrength
) => {
  const result = calculateMaxLengthOfOneFenceNeeded(
    widthOfPanel,
    lengthOfPanel,
    pitch,
    grndSnowLoad,
    rainLoad,
    fenceStrength
  );
  if (isNaN(result)) return result;
  return (2 * result).toFixed(1);
};

// distance between 1st and 2nd fence
export const calculateDistanceBetweenFirstTwoFences = (lengthOfPanel, fences) => {
  if (parseInt(fences) <= 0) {
    return 'NA';
  } else {
    return (parseFloat(lengthOfPanel) - 1) / (Math.ceil(parseFloat(fences)) * 2 - 1);
  }
};

// distance between additional fences/ridge
export const calculateDistanceBetweenAdditionalFences = (lengthOfPanel, fences) => {
  const result = calculateDistanceBetweenFirstTwoFences(lengthOfPanel, fences);

  if (result === 'NA') return result;

  return result * 2;
};

/**
 * calucultes value for H cells and if doubleForJ is true it calculates for J cells as,
 * only diff between them is one is double of other
 *
 * @param {number} widthOfPanel
 * @param {number} lengthOfPanel
 * @param {number} grndSnowLoad
 * @param {number} rainLoad
 * @param {number} FenceStrength
 * @param {number} G
 * @param {boolean} doubleForJ
 * @returns number or empty string
 */
const calculateH = (
  widthOfPanel,
  lengthOfPanel,
  grndSnowLoad,
  rainLoad,
  fenceStrength,
  G,
  doubleForJ = false
) => {
  if (
    rainLoad === '' ||
    fenceStrength === '' ||
    getIsAnyValueFalseOrInfinity([widthOfPanel, lengthOfPanel, grndSnowLoad])
  ) {
    return 'error';
  } else {
    const result =
      (parseFloat(fenceStrength) /
        ((parseFloat(grndSnowLoad) + parseFloat(rainLoad)) *
          Math.cos(Math.atan(parseFloat(G) / 12)) *
          (parseFloat(widthOfPanel) / 12) *
          Math.sin(Math.atan(parseFloat(G) / 12)))) *
      12;
    if (doubleForJ) return Math.floor((result * 2) / 0.25) * 0.25;
    else return Math.floor(result / 0.25) * 0.25;
  }
};

export const calculateMaxLengthsForOneFence = (
  widthOfPanel,
  lengthOfPanel,
  grndSnowLoad,
  rainLoad,
  FenceStrength
) => {
  return Slopes_Gs.map((value) =>
    convertToFeetAndInches(
      calculateH(widthOfPanel, lengthOfPanel, grndSnowLoad, rainLoad, FenceStrength, value)
    )
  );
};

export const calculateMaxLengthsForTwoFence = (
  widthOfPanel,
  lengthOfPanel,
  grndSnowLoad,
  rainLoad,
  FenceStrength
) => {
  return Slopes_Gs.map((value) =>
    convertToFeetAndInches(
      calculateH(widthOfPanel, lengthOfPanel, grndSnowLoad, rainLoad, FenceStrength, value, true)
    )
  );
};

const convertToFeetAndInches = (cellValue) => {
  if (cellValue === '' || isNaN(cellValue)) {
    return 'error';
  } else {
    const feet = Math.floor(cellValue / 12);
    const inches = cellValue % 12;
    return `${feet}' ${inches}"`;
  }
};

export const calculateNumberOfBrackets = (eveLength, widthOfPanel, numberOfFences) => {
  const numbfence = Math.ceil(numberOfFences);
  return ((eveLength * 12) / widthOfPanel) * numbfence;
};

export const calculateNumberOfTubing = (eveLength, barSystem, numberOfFences) => {
  const numbfence = Math.ceil(numberOfFences);

  return ((eveLength * numbfence) / 10) * barSystem;
};

export const calculateStopEnds = (barSystem, numberOfFences, eveLength) => {
  const numbfence = Math.ceil(numberOfFences);
  if (eveLength > 50) {
    const xFactor = eveLength / 50;
    return numbfence * 2 * barSystem * xFactor;
  }
  return numbfence * 2 * barSystem;
};

export const calculateIceFlags = (numberOfBrackets, numberOfFences) => {
  const numbfence = Math.ceil(numberOfFences);

  return numberOfBrackets / numbfence;
};

export const combineFenceOutput = (
  widthOfPanel,
  lengthOfPanel,
  pitch,
  grndSnowLoad,
  rainLoad,
  eveLength,
  barSystem,
  fenceStrength
) => {
  //
  const numberOfFencesNeeded = calculateNumberOfFencesNeeded(
    widthOfPanel,
    lengthOfPanel,
    pitch,
    grndSnowLoad,
    rainLoad,
    fenceStrength
  );
  const numberOfBrackets = calculateNumberOfBrackets(eveLength, widthOfPanel, numberOfFencesNeeded);
  const numberOfTubing = calculateNumberOfTubing(eveLength, barSystem, numberOfFencesNeeded);
  const stopEndsAccessories = calculateStopEnds(barSystem, numberOfFencesNeeded, eveLength);
  const iceFlags = calculateIceFlags(numberOfBrackets, numberOfFencesNeeded);
  //
  const maxLengthOfOneFenceNeeded = calculateMaxLengthOfOneFenceNeeded(
    widthOfPanel,
    lengthOfPanel,
    pitch,
    grndSnowLoad,
    rainLoad || '',
    fenceStrength
  );
  //
  const maxLengthOfTwoFenceNeeded = calculateMaxLengthOfTwoFenceNeeded(
    widthOfPanel,
    lengthOfPanel,
    pitch,
    grndSnowLoad,
    rainLoad || '',
    fenceStrength
  );
  const distanceBetweenFirstTwoFences = calculateDistanceBetweenFirstTwoFences(
    lengthOfPanel,
    Math.ceil(numberOfFencesNeeded)
  );

  const distanceBetweenAdditionalFences = calculateDistanceBetweenAdditionalFences(
    lengthOfPanel,
    Math.ceil(numberOfFencesNeeded)
  );

  return [
    numberOfFencesNeeded,
    maxLengthOfOneFenceNeeded,
    maxLengthOfTwoFenceNeeded,
    distanceBetweenFirstTwoFences,
    distanceBetweenAdditionalFences,
    numberOfBrackets,
    numberOfTubing,
    stopEndsAccessories,
    iceFlags
  ];
};
